import { Checkbox, DefaultButton, Dropdown, IconButton, Label, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, Stack } from '@fluentui/react';
import * as React from 'react';
import { getAccreditationRuleDefinitionLabel, getAccreditorFieldDefinitions, getAccreditorRules, getAccreditors, getListOfCriteriaDefinitions, updateAccreditorRules } from '../data/accreditorRules';
import { IAccreditor, IAccreditorRule, IAccreditorRuleFieldDefinition, ICriteria } from '../types/IAccreditor';
import { ICourse } from '../types/ICourse';

const AccreditationRule: React.FunctionComponent<{
    accreditorFieldDefinition: IAccreditorRuleFieldDefinition,
    criteriaDefinitions: ICriteria[],
    onRemoveRule: (accreditorRule: IAccreditorRule) => void,
    onChangeRule: (accreditorRule: IAccreditorRule) => void,
    accreditorRule: IAccreditorRule
}> = ({ accreditorFieldDefinition, criteriaDefinitions, onRemoveRule, onChangeRule, accreditorRule }) => {

    return <Stack tokens={{ childrenGap: 6 }}>
        <br />
        <Stack horizontal >
            <Label>{getAccreditationRuleDefinitionLabel(accreditorFieldDefinition)}</Label>
            <IconButton iconProps={{ iconName: "Delete" }} onClick={() => {
                onRemoveRule(accreditorRule);
            }} />
        </Stack>
        <Stack.Item>
            <Label>Criteria</Label>
            <Stack tokens={{ childrenGap: 6 }}>
                {criteriaDefinitions.map(cd => {
                    return <Stack horizontal tokens={{ childrenGap: 6 }}>
                        <Checkbox key={`criteria-def-${cd.CriteriaDefinitionId}`}
                            checked={accreditorRule.Criteria.CriteriaDefinitionId === cd.CriteriaDefinitionId && (accreditorRule.Criteria.Criteria === cd.Criteria || accreditorRule.Criteria.Criteria === "Both")}
                            label={cd.Criteria}
                            onChange={(_ev, checked) => {
                                accreditorRule.Criteria = {
                                    CriteriaColor: cd.CriteriaColor,
                                    Criteria: checked ? cd.Criteria : "",
                                    CriteriaDefinitionId: cd.CriteriaDefinitionId
                                }
                                onChangeRule(accreditorRule);
                            }} /><div style={{ height: 20, width: 20, backgroundColor: cd.CriteriaColor }}></div></Stack>
                })}
            </Stack>
        </Stack.Item>
        <Stack.Item>
            <Label>Starting Point</Label>
            <Checkbox label='Is Starting Point' checked={accreditorRule.IsStartingPoint} onChange={(_ev, checked) => {
                accreditorRule.IsStartingPoint = checked || false;
                onChangeRule(accreditorRule);
            }} />
        </Stack.Item>
    </Stack>
}

const AccreditationRulesPanel: React.FunctionComponent<{ course: ICourse, onClose: () => void, onRefresh: () => void }> = ({ course, onClose, onRefresh }) => {

    const [accreditors, setAccreditors] = React.useState<IAccreditor[]>([]);
    const [accreditorFieldDefinitions, setAccreditorFieldDefinitions] = React.useState<IAccreditorRuleFieldDefinition[]>([]);
    const [accreditorCriteriaDefinitions, setAccreditorCriteriaDefinitions] = React.useState<ICriteria[]>([]);
    const [accreditorRules, setAccreditorRules] = React.useState<IAccreditorRule[]>([]);
    const [selectedAccreditor, setSelectedAccreditor] = React.useState<IAccreditor>();
    const [error, setError] = React.useState<any>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    let originalRules = React.useRef<IAccreditorRule[]>([]);

    React.useEffect(() => {
        Promise.all([getAccreditors(), getAccreditorFieldDefinitions(), getListOfCriteriaDefinitions()]).then(res => {
            setAccreditors(res[0]);
            setAccreditorFieldDefinitions(res[1]);
            setAccreditorCriteriaDefinitions(res[2]);
            if(res[0].length === 1)
            {
                setSelectedAccreditor(res[0][0])
            }
        }).catch(err => setError(err));
    }, [])

    React.useEffect(() => {
        if (selectedAccreditor) {
            getAccreditorRules(selectedAccreditor).then(rules => {
                const currentRules:  IAccreditorRule[] = [], newRules: IAccreditorRule[] = [];
                rules.forEach(ar => {
                    if(ar.Course === course.SIS && ar.Accreditor.AccreditorId === selectedAccreditor.AccreditorId)
                    {
                        currentRules.push({...ar});
                        newRules.push({...ar});
                    }
                })
                
                currentRules.sort((a, b) => a.AccreditorRuleDefinition.Category > b.AccreditorRuleDefinition.Category ? 1 : -1);                                         
                newRules.sort((a, b) => a.AccreditorRuleDefinition.Category > b.AccreditorRuleDefinition.Category ? 1 : -1);       
                originalRules.current = currentRules;
                setAccreditorRules(newRules);
            }).catch(err => {
                setError(err)
            })
        }
    }, [selectedAccreditor, course])

    const definitionsWithAssociatedRules = accreditorFieldDefinitions.filter(afd => accreditorRules.find(r => r.Accreditor.AccreditorId === selectedAccreditor?.AccreditorId && r.AccreditorRuleDefinition.AccreditationFieldDefinitionId === afd.AccreditationFieldDefinitionId && r.Course === course.SIS) !== undefined);
    const definitionsWithoutAssociatedRules = accreditorFieldDefinitions.filter(afd => accreditorRules.find(r => r.Accreditor.AccreditorId === selectedAccreditor?.AccreditorId && r.AccreditorRuleDefinition.AccreditationFieldDefinitionId === afd.AccreditationFieldDefinitionId && r.Course === course.SIS) === undefined);
    let optionsAvailable = definitionsWithoutAssociatedRules.map(a => ({ text: getAccreditationRuleDefinitionLabel(a), key: a.AccreditationFieldDefinitionId }));
    optionsAvailable.push({ text: "Choose a definition...", key: -1 })

    console.log("originalRules", originalRules.current);
    console.log("accreditorRules", accreditorRules)
    const isSubmitButtonDisabled = !selectedAccreditor || JSON.stringify(originalRules.current) === JSON.stringify(accreditorRules);

    return <Panel
        isOpen={true}
        type={PanelType.medium}
        onDismiss={onClose}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack tokens={{ childrenGap: 6 }}>
                {error && <MessageBar messageBarType={MessageBarType.error}>{error.message || error}</MessageBar>}
                {successMessage && <MessageBar messageBarType={MessageBarType.success}>{successMessage}</MessageBar>}
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton 
                        text='Save' 
                        split
                        menuProps={{
                            items: [
                                {
                                    key: 'saveAndClose',
                                    text: 'Save and Close',
                                    iconProps: { iconName: 'SaveAndClose' },
                                    onClick: () => {
                                        if (selectedAccreditor) {
                                            updateAccreditorRules(course.SIS, selectedAccreditor.AccreditorId, accreditorRules).then((res: any) => {
                                                if (res.name === "RequestError") {
                                                    setError(JSON.stringify(res));
                                                }
                                                else {
                                                    originalRules.current = accreditorRules;
                                                    setSuccessMessage(`Successfully updated rules for ${selectedAccreditor.AccreditorName} on course ${course.SIS}`);
                                                    onRefresh();
                                                    onClose();
                                                }
                                            }).catch((err: any) => setError(err));
                                        }
                                    }
                                }]
                        }}
                        disabled={isSubmitButtonDisabled} 
                        onClick={() => {
                        if (selectedAccreditor) {
                            updateAccreditorRules(course.SIS, selectedAccreditor.AccreditorId, accreditorRules).then((res: any) => {
                                if (res.name === "RequestError") {
                                    setError(JSON.stringify(res));
                                }
                                else {
                                    originalRules.current = accreditorRules;
                                    setSuccessMessage(`Successfully updated rules for ${selectedAccreditor.AccreditorName} on course ${course.SIS}`);
                                    onRefresh()
                                }
                            }).catch((err: any) => setError(err));
                        }
                    }} />
                    <DefaultButton text='Cancel' onClick={onClose} />
                </Stack></Stack>
        }}
        headerText={`Acceditation Rules: ${course.Title}`}>
        <Stack>
            <Stack.Item>
                <Label>Course Information</Label>
                <span>{course.Title} ({course.SIS})</span>
            </Stack.Item>
            <Dropdown
                label='Accreditor'
                placeholder='Choose an accreditor...'
                selectedKey={selectedAccreditor?.AccreditorId}
                options={accreditors.map(a => ({ text: a.AccreditorName, key: a.AccreditorId }))}
                onChange={(_, option) => {
                    const accreditor = accreditors.find(a => a.AccreditorId === option?.key);
                    setSelectedAccreditor(accreditor)
                }} />
            {selectedAccreditor && <Dropdown
                label='Rule Definitions'
                placeholder='Choose a rule definition to add...'
                options={optionsAvailable}
                selectedKey={-1}
                onChange={(_, option) => {
                    const selectedDefinition = definitionsWithoutAssociatedRules.find(a => a.AccreditationFieldDefinitionId === option?.key);
                    if (selectedDefinition) {
                        let accreditorRule: IAccreditorRule = {
                            Accreditor: selectedAccreditor,
                            AccreditorRuleDefinition: selectedDefinition,
                            Criteria: { CriteriaColor: "", Criteria: "", CriteriaDefinitionId: -1 },
                            IsStartingPoint: false,
                            Course: course.SIS
                        }
                        setAccreditorRules([...accreditorRules, accreditorRule])
                    }
                }} />}
            {selectedAccreditor && definitionsWithAssociatedRules.map(afd => {
                const associatedRule = accreditorRules.find(r => r.Accreditor.AccreditorId === selectedAccreditor?.AccreditorId && r.AccreditorRuleDefinition.AccreditationFieldDefinitionId === afd.AccreditationFieldDefinitionId && r.Course === course.SIS);
                let retVal = undefined;
                if (associatedRule) {
                    retVal = <AccreditationRule
                        key={`AccreditationRule-${associatedRule.AccreditorRuleDefinition.AccreditationFieldDefinitionId}`}
                        criteriaDefinitions={accreditorCriteriaDefinitions}
                        accreditorFieldDefinition={afd}
                        accreditorRule={associatedRule}
                        onChangeRule={(ruleToChange) => {
                            let newAccreditorRule = [...accreditorRules].find(ar => ar.AccreditorRuleDefinition === ruleToChange.AccreditorRuleDefinition);
                            if (newAccreditorRule) {
                                let allRulesExceptNewRule = [...accreditorRules].filter(ar => ar.AccreditorRuleDefinition !== ruleToChange.AccreditorRuleDefinition);
                                setAccreditorRules([...allRulesExceptNewRule, newAccreditorRule]);
                            }
                            setSuccessMessage(undefined);
                        }}
                        onRemoveRule={(ruleToRemove) => {
                            let newAccreditorRules = [...accreditorRules].filter(ar => ar.AccreditorRuleDefinition !== ruleToRemove.AccreditorRuleDefinition);
                            setAccreditorRules(newAccreditorRules);
                            setSuccessMessage(undefined);
                        }} />
                }
                return retVal;
            })}
        </Stack>
    </Panel>
};

export default AccreditationRulesPanel;